import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useEffect, useState } from "react";
import wp from "wp";
import { __ } from "./utils/i18n.js";
import { rmlOpts } from "./utils/rmlOpts.js";
const { blockEditor, blocks, components, editor, i18n } = wp;
const BLOCK_ID = "real-media-library/gallery";
const { registerBlockType } = blocks;
const { G, SVG, Path, ServerSideRender: ServerSideRenderWpLower6Dot2, PanelBody, RangeControl, ToggleControl, SelectControl, TreeSelect, Notice, Spinner, Button, withNotices } = components;
const { InspectorControls, ServerSideRender: ServerSideRenderWpGreaterEqual6Dot2, useBlockProps: useBlockPropsWpLower6Dot2 } = editor;
const blockEditorModule = blockEditor || {};
const { useBlockProps: useBlockPropsWpGreaterEqual6Dot2 } = blockEditorModule;
const { __: wp__ } = i18n;
const castWp = (component)=>component;
const WpInspectorControls = castWp(InspectorControls);
const WpPanelBody = castWp(PanelBody);
const WpRangeControl = castWp(RangeControl);
const WpToggleControl = castWp(ToggleControl);
const WpSelectControl = castWp(SelectControl);
const WpButton = castWp(Button);
const WpServerSideRender = castWp(ServerSideRenderWpLower6Dot2 || ServerSideRenderWpGreaterEqual6Dot2);
const WpNotice = castWp(Notice);
const WpSvg = castWp(SVG);
const WpPath = castWp(Path);
const WpG = castWp(G);
const useBlockProps = useBlockPropsWpLower6Dot2 || useBlockPropsWpGreaterEqual6Dot2;
const linkOptions = [
    {
        value: "attachment",
        label: wp__("Attachment Page")
    },
    {
        value: "media",
        label: wp__("Media File")
    },
    {
        value: "none",
        label: wp__("None")
    }
];
function SelectFolderControl({ value, onChange, ...rest }) {
    const [busy, setBusy] = useState(true);
    const [tree, setTree] = useState([]);
    useEffect(()=>{
        let cancelled = false;
        (async ()=>{
            try {
                const { tree: loadedTree } = await window.wp.apiFetch({
                    path: `/realmedialibrary/v1/tree`
                });
                if (cancelled) {
                    return;
                }
                const nextTree = [
                    ...loadedTree
                ];
                nextTree.unshift({
                    id: -1,
                    name: __("Unorganized")
                });
                nextTree.unshift({
                    id: undefined,
                    name: "-"
                });
                setTree(nextTree);
            } catch  {
                if (!cancelled) {
                    setTree([]);
                }
            } finally{
                if (!cancelled) {
                    setBusy(false);
                }
            }
        })();
        return ()=>{
            cancelled = true;
        };
    }, []);
    const TreeSelectControl = TreeSelect;
    const SpinnerControl = Spinner;
    return !busy ? /*#__PURE__*/ _jsx(TreeSelectControl, {
        label: __("Folder"),
        ...rest,
        value: value,
        onChange: onChange,
        tree: tree
    }) : /*#__PURE__*/ _jsx(SpinnerControl, {});
}
/**
 * Gallery edit view with inspector controls like the normal gallery block.
 */ function GalleryEdit(props) {
    const { attributes, setAttributes } = props;
    const { fid, columns = 3, imageCrop, captions, linkTo } = attributes;
    const blockProps = useBlockProps ? useBlockProps() : {};
    const setFid = (nextFid)=>setAttributes({
            fid: +nextFid
        });
    const setLinkTo = (nextLinkTo)=>setAttributes({
            linkTo: nextLinkTo
        });
    const setColumnsNumber = (nextColumns)=>setAttributes({
            columns: nextColumns
        });
    const toggleImageCrop = ()=>setAttributes({
            imageCrop: !imageCrop
        });
    const toggleCaptions = ()=>setAttributes({
            captions: !captions
        });
    const handleReload = ()=>setAttributes({
            lastEditReload: new Date().getTime()
        });
    return /*#__PURE__*/ _jsxs(Fragment, {
        children: [
            /*#__PURE__*/ _jsx(WpInspectorControls, {
                children: /*#__PURE__*/ _jsxs(WpPanelBody, {
                    title: wp__("Gallery Settings"),
                    children: [
                        /*#__PURE__*/ _jsx(SelectFolderControl, {
                            value: fid,
                            onChange: setFid
                        }),
                        /*#__PURE__*/ _jsx(WpRangeControl, {
                            label: wp__("Columns"),
                            value: columns,
                            onChange: setColumnsNumber,
                            min: "1",
                            max: "8"
                        }),
                        /*#__PURE__*/ _jsx(WpToggleControl, {
                            label: wp__("Crop Images"),
                            checked: !!imageCrop,
                            onChange: toggleImageCrop
                        }),
                        /*#__PURE__*/ _jsx(WpToggleControl, {
                            label: wp__("Caption"),
                            checked: !!captions,
                            onChange: toggleCaptions
                        }),
                        /*#__PURE__*/ _jsx(WpSelectControl, {
                            label: wp__("Link To"),
                            value: linkTo,
                            onChange: setLinkTo,
                            options: linkOptions
                        }),
                        /*#__PURE__*/ _jsx(WpButton, {
                            isPrimary: true,
                            onClick: handleReload,
                            children: __("Reload content")
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                ...blockProps,
                children: [
                    /*#__PURE__*/ _jsx(WpServerSideRender, {
                        block: BLOCK_ID,
                        attributes: attributes
                    }),
                    !fid && /*#__PURE__*/ _jsx(WpNotice, {
                        status: "error",
                        isDismissible: false,
                        children: /*#__PURE__*/ _jsx("p", {
                            children: __("Please select a folder (media library) in the block settings.")
                        })
                    })
                ]
            })
        ]
    });
}
/**
 * @see https://github.com/WordPress/gutenberg/blob/master/packages/block-library/src/gallery/index.js
 */ const settings = {
    title: "Real Media Library Gallery",
    description: "Display folder images in a rich gallery.",
    icon: /*#__PURE__*/ _jsxs(WpSvg, {
        viewBox: "0 0 24 24",
        xmlns: "http://www.w3.org/2000/svg",
        children: [
            /*#__PURE__*/ _jsx(WpPath, {
                fill: "none",
                d: "M0 0h24v24H0V0z"
            }),
            /*#__PURE__*/ _jsxs(WpG, {
                children: [
                    /*#__PURE__*/ _jsx(WpPath, {
                        d: "M20 4v12H8V4h12m0-2H8L6 4v12l2 2h12l2-2V4l-2-2z"
                    }),
                    /*#__PURE__*/ _jsx(WpPath, {
                        d: "M12 12l1 2 3-3 3 4H9z"
                    }),
                    /*#__PURE__*/ _jsx(WpPath, {
                        d: "M2 6v14l2 2h14v-2H4V6H2z"
                    })
                ]
            })
        ]
    }),
    category: "common",
    supports: {
        align: true
    },
    attributes: {
        fid: {
            type: "number",
            default: 0
        },
        columns: {
            type: "number",
            default: 3
        },
        imageCrop: {
            type: "boolean",
            default: true
        },
        captions: {
            type: "boolean",
            default: true
        },
        linkTo: {
            type: "string",
            default: "none"
        },
        lastEditReload: {
            type: "number",
            default: 0
        }
    },
    edit: withNotices(GalleryEdit),
    save () {
        // Rendering in PHP, see general\Gutenberg
        return null;
    }
};
if (typeof rmlOpts !== "undefined" && rmlOpts?.others?.isBlockApiVersion3Supported) {
    settings.apiVersion = 3;
}
registerBlockType(BLOCK_ID, settings);
